import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { Crown, Link2, Search, Sparkles } from "lucide-react";
import { toast } from "sonner";
import AffiliatesPanel from "@/components/AffiliatesPanel";
import BillingHealthPanel from "@/components/BillingHealthPanel";
import EmailHealthCard from "@/components/EmailHealthCard";

export default function Admin() {
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tplName, setTplName] = useState("");
  const [tplKind, setTplKind] = useState("workout");
  const [tplData, setTplData] = useState("{\n}");
  const [assignFor, setAssignFor] = useState({});
  const [pushMetrics, setPushMetrics] = useState(null);

  // Owner controls
  const [grantEmail, setGrantEmail] = useState("");
  const [granting, setGranting] = useState(false);
  const [coachEmail, setCoachEmail] = useState("");
  const [clientQuery, setClientQuery] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const coaches = useMemo(() => users.filter((u) => u.is_coach), [users]);
  const usersById = useMemo(() => {
    const m = {};
    users.forEach((u) => { m[u.id] = u; });
    return m;
  }, [users]);

  const load = async () => {
    const [a, u, t, m] = await Promise.all([
      api.get("/admin/analytics"),
      api.get("/admin/users"),
      api.get("/admin/templates"),
      api.get("/admin/push_metrics").catch(() => ({ data: null })),
    ]);
    setAnalytics(a.data);
    setUsers(u.data);
    setTemplates(t.data);
    setPushMetrics(m.data);
  };
  useEffect(() => {
    load();
  }, []);

  const saveTemplate = async () => {
    try {
      const data = JSON.parse(tplData || "{}");
      await api.post("/admin/templates", { name: tplName, kind: tplKind, data });
      toast.success("Template saved");
      setTplName("");
      setTplData("{\n}");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid JSON");
    }
  };

  const assign = async (tplId) => {
    const userId = assignFor[tplId];
    if (!userId) return toast.error("Select a user first");
    try {
      await api.post("/admin/templates/assign", { template_id: tplId, user_id: userId });
      toast.success("Assigned");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assignment failed");
    }
  };

  const grantOwnerCoach = async () => {
    if (!grantEmail) return toast.error("Enter an email");
    setGranting(true);
    try {
      const r = await api.post("/admin/grant-owner-coach", { email: grantEmail.trim() });
      toast.success(`${r.data.user.email} is now an owner-coach (code ${r.data.coach_code})`);
      setGrantEmail("");
      setCoachEmail(r.data.user.email);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not grant");
    } finally {
      setGranting(false);
    }
  };

  const searchClients = async (q) => {
    setClientQuery(q);
    if (!q || q.length < 2) {
      setClientResults([]);
      return;
    }
    setSearching(true);
    try {
      const r = await api.get(`/admin/users/search?q=${encodeURIComponent(q)}`);
      // Only show clients (non-coach accounts) in this picker
      setClientResults((r.data || []).filter((u) => !u.is_coach));
    } catch {
      setClientResults([]);
    } finally {
      setSearching(false);
    }
  };

  const linkClient = async (clientEmail) => {
    if (!coachEmail) return toast.error("Choose a coach first");
    try {
      await api.post("/admin/link-client", {
        coach_email: coachEmail,
        client_email: clientEmail,
      });
      toast.success(`${clientEmail} → ${coachEmail}`);
      setClientQuery("");
      setClientResults([]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Link failed");
    }
  };

  const unlinkClient = async (clientEmail) => {
    try {
      await api.post("/admin/unlink-client", {
        coach_email: coachEmail || "x@x.com",
        client_email: clientEmail,
      });
      toast.success(`Unlinked ${clientEmail}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unlink failed");
    }
  };

  const seedDemo = async (clientEmail) => {
    try {
      const r = await api.post("/admin/seed-demo-data", { client_email: clientEmail });
      toast.success(`Seeded ${r.data.workouts_added} workouts + ${r.data.checkins_added} check-ins`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="overline text-blaze">/ Admin</div>
        <h1 className="font-heading text-5xl uppercase mt-1">Control room.</h1>
      </div>

      <div data-testid={ADMIN.analyticsCard} className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="stat-tile"><div className="overline">Users</div><div className="font-data text-3xl">{analytics?.total_users ?? 0}</div></div>
        <div className="stat-tile"><div className="overline">Workouts</div><div className="font-data text-3xl">{analytics?.total_workouts ?? 0}</div></div>
        <div className="stat-tile"><div className="overline">Meal plans</div><div className="font-data text-3xl">{analytics?.total_meal_plans ?? 0}</div></div>
        <div className="stat-tile"><div className="overline">Active subs</div><div className="font-data text-3xl text-blaze">{analytics?.active_subscribers ?? 0}</div></div>
        <div className="stat-tile"><div className="overline">Revenue</div><div className="font-data text-3xl">${analytics?.total_revenue_usd?.toFixed(2) ?? "0.00"}</div></div>
      </div>

      <EmailHealthCard />

      <BillingHealthPanel />

      <AffiliatesPanel />

      {pushMetrics && (
        <div className="obs-card" data-testid="push-metrics">
          <div className="overline mb-2">Push delivery (last 7 days)</div>
          <h3 className="font-heading text-2xl uppercase mb-4">VAPID metrics</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="stat-tile"><div className="overline">Total sent</div><div className="font-data text-3xl">{pushMetrics.total_sent}</div></div>
            <div className="stat-tile"><div className="overline">Delivered</div><div className="font-data text-3xl text-blaze">{pushMetrics.total_delivered}</div></div>
            <div className="stat-tile"><div className="overline">Failed</div><div className="font-data text-3xl">{pushMetrics.total_failed}</div></div>
            <div className="stat-tile"><div className="overline">Delivery rate</div><div className="font-data text-3xl">{pushMetrics.delivery_rate}%</div></div>
          </div>
          {pushMetrics.by_day?.length > 0 && (
            <div className="mt-4 grid grid-cols-7 gap-2">
              {pushMetrics.by_day.map((d) => (
                <div key={d.date} className="border border-obsidian-600 rounded-sm p-2 text-center">
                  <div className="text-[10px] text-zinc-500 uppercase tracking-widest">{d.date.slice(5)}</div>
                  <div className="font-data text-lg">{d.delivered}/{d.total}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="obs-card" data-testid="owner-controls">
        <div className="overline mb-2 text-blaze">/ Owner controls</div>
        <h3 className="font-heading text-2xl uppercase mb-4">Comp accounts & client linking</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="overline mb-1">1. Grant owner-coach</div>
            <p className="text-xs text-zinc-500 mb-3">
              Promotes an existing account to admin + coach with a 100-year active subscription.
              No card needed; full coach UI unlocked.
            </p>
            <div className="flex gap-2">
              <input
                data-testid="grant-owner-coach-email"
                type="email"
                value={grantEmail}
                onChange={(e) => setGrantEmail(e.target.value)}
                placeholder="email@example.com"
                className="flex-1 bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze text-sm"
              />
              <button
                data-testid="grant-owner-coach-submit"
                disabled={granting}
                onClick={grantOwnerCoach}
                className="blaze-btn px-4 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50"
              >
                <Crown className="h-3.5 w-3.5" /> {granting ? "Granting…" : "Grant"}
              </button>
            </div>
            {coaches.length > 0 && (
              <div className="mt-3 text-[11px] text-zinc-500 uppercase tracking-widest">
                Current coaches: {coaches.map((c) => c.email).join(", ")}
              </div>
            )}
          </div>

          <div>
            <div className="overline mb-1">2. Link a client to a coach</div>
            <p className="text-xs text-zinc-500 mb-3">
              Pick a coach, then search a client by email and tap Link.
            </p>
            <select
              data-testid="link-client-coach-select"
              value={coachEmail}
              onChange={(e) => setCoachEmail(e.target.value)}
              className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze text-sm mb-2"
            >
              <option value="">— Select coach —</option>
              {coaches.map((c) => (
                <option key={c.id} value={c.email}>{c.email}</option>
              ))}
            </select>

            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
              <input
                data-testid="link-client-search"
                type="text"
                value={clientQuery}
                onChange={(e) => searchClients(e.target.value)}
                placeholder="Search client email…"
                className="w-full bg-obsidian-900 border border-obsidian-600 pl-9 pr-3 py-2 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze text-sm"
              />
            </div>

            {clientQuery && (
              <div className="mt-2 border border-obsidian-600 rounded-sm max-h-56 overflow-auto">
                {searching && <div className="p-2 text-xs text-zinc-500">Searching…</div>}
                {!searching && clientResults.length === 0 && (
                  <div className="p-2 text-xs text-zinc-500">No matches.</div>
                )}
                {clientResults.map((c) => {
                  const linkedTo = c.coach_id ? usersById[c.coach_id]?.email : null;
                  return (
                    <div key={c.id} className="flex items-center justify-between gap-2 p-2 border-b border-obsidian-600 last:border-b-0">
                      <div className="text-xs">
                        <div className="text-zinc-100">{c.email}</div>
                        <div className="text-[10px] text-zinc-500 uppercase tracking-widest">
                          {linkedTo ? `Linked → ${linkedTo}` : "Unlinked"} · {c.subscription?.status || "—"}
                        </div>
                      </div>
                      {linkedTo === coachEmail ? (
                        <div className="flex gap-1.5 shrink-0">
                          <button
                            data-testid={`seed-demo-${c.id}`}
                            onClick={() => seedDemo(c.email)}
                            title="Insert demo workouts + check-ins"
                            className="ghost-btn px-2.5 py-1.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-1"
                          >
                            <Sparkles className="h-3 w-3" /> Seed
                          </button>
                          <button
                            data-testid={`link-client-unlink-${c.id}`}
                            onClick={() => unlinkClient(c.email)}
                            className="ghost-btn px-2.5 py-1.5 text-[10px] uppercase tracking-widest"
                          >
                            Unlink
                          </button>
                        </div>
                      ) : (
                        <button
                          data-testid={`link-client-link-${c.id}`}
                          disabled={!coachEmail}
                          onClick={() => linkClient(c.email)}
                          className="blaze-btn px-3 py-1.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-50"
                        >
                          <Link2 className="h-3 w-3" /> Link
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="obs-card">
        <div className="overline mb-2">Users</div>
        <h3 className="font-heading text-2xl uppercase mb-4">Members</h3>
        <div data-testid={ADMIN.usersTable} className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-zinc-500 uppercase text-[10px] tracking-widest">
              <tr>
                <th className="text-left py-2 pr-3">Email</th>
                <th className="text-left py-2 pr-3">Name</th>
                <th className="text-left py-2 pr-3">Plan</th>
                <th className="text-left py-2 pr-3">Status</th>
                <th className="text-left py-2 pr-3">Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-obsidian-600">
                  <td className="py-2 pr-3">{u.email}</td>
                  <td className="py-2 pr-3">{u.first_name || "—"}</td>
                  <td className="py-2 pr-3 uppercase">{u.subscription?.plan || "free"}</td>
                  <td className="py-2 pr-3">
                    <span className={`text-xs uppercase tracking-widest ${u.subscription?.status === "active" ? "text-blaze" : "text-zinc-500"}`}>
                      {u.subscription?.status || "inactive"}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-zinc-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="obs-card">
        <div className="overline mb-2">Templates</div>
        <h3 className="font-heading text-2xl uppercase mb-4">Create template</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <input
            data-testid={ADMIN.templateName}
            value={tplName}
            onChange={(e) => setTplName(e.target.value)}
            placeholder="Template name"
            className="bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze"
          />
          <select
            data-testid={ADMIN.templateKind}
            value={tplKind}
            onChange={(e) => setTplKind(e.target.value)}
            className="bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze"
          >
            <option value="workout">Workout</option>
            <option value="meal">Meal</option>
          </select>
          <button data-testid={ADMIN.templateSubmit} onClick={saveTemplate} className="blaze-btn px-4 py-2 text-xs">Save</button>
        </div>
        <textarea
          data-testid={ADMIN.templateData}
          value={tplData}
          onChange={(e) => setTplData(e.target.value)}
          rows={6}
          className="w-full font-mono text-xs bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze"
          placeholder='{ "exercises": [...] }'
        />

        {templates.length > 0 && (
          <div className="mt-5 space-y-2">
            {templates.map((t) => (
              <div key={t.id} className="border border-obsidian-600 p-3 rounded-sm">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="font-heading text-lg uppercase">{t.name}</div>
                    <div className="overline">{t.kind}</div>
                  </div>
                  <div className="text-xs text-zinc-500">{new Date(t.created_at).toLocaleDateString()}</div>
                </div>
                <div className="flex flex-col md:flex-row gap-2">
                  <select
                    data-testid={`${ADMIN.assignUserSelect}-${t.id}`}
                    value={assignFor[t.id] || ""}
                    onChange={(e) => setAssignFor((s) => ({ ...s, [t.id]: e.target.value }))}
                    className="flex-1 bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm text-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze"
                  >
                    <option value="">Select user to assign…</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>{u.email}</option>
                    ))}
                  </select>
                  <button
                    data-testid={`${ADMIN.assignTemplate}-${t.id}`}
                    onClick={() => assign(t.id)}
                    className="ghost-btn px-4 py-2 text-xs"
                  >
                    Assign
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
